import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Globe, Database } from "lucide-react";
import FAQSection from "../../components/common/sections/FAQ";
import { getEmailExchangePlans } from "@/services/dreamscapeApi";
import { CurrencyCode } from "@/utils/currencyConverter";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/domain/emailExchange.webp";
import SEO from "@/components/common/seo/seo";

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

export default function EmailExchange() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [_selectedCurrency, _setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [_isDummyData, setIsDummyData] = useState(false);

    useEffect(() => {
        // Load Email Exchange plans from API
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);
            
            try {
                // Try to fetch real data from Dreamscape API (sandomains.com)
                const result = await getEmailExchangePlans({ limit: 50 });
                
                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    // If API returns no plans, fall back to dummy data
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                // If API call fails, fall back to dummy data
                console.error('Failed to load plans from API, using dummy data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load plans');
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        // Fallback dummy data function
        const loadDummyPlans = () => {
            const dummyPlans = [
                {
                    id: 1,
                    name: "Email Exchange Starter",
                    description: "Business-Class Emails for Your Own Domain Name. Perfect for small teams.",
                    periods: [
                        { period: 1, price: { register: 4.99, renew: 6.99 } },
                        { period: 12, price: { register: 3.99, renew: 5.99 } },
                        { period: 24, price: { register: 3.49, renew: 5.49 } },
                        { period: 36, price: { register: 2.99, renew: 4.99 } }
                    ],
                    features: [
                        { name: "Email Storage", value: "25 GB" },
                        { name: "Email Accounts", value: "1" },
                        { name: "Calendar & Contacts", value: "Yes" },
                        { name: "Mobile Sync", value: "Yes" }
                    ]
                },
                {
                    id: 2,
                    name: "Email Exchange Business",
                    description: "Ideal for growing businesses. More storage and collaboration features.",
                    periods: [
                        { period: 1, price: { register: 9.99, renew: 12.99 } },
                        { period: 12, price: { register: 8.99, renew: 11.99 } },
                        { period: 24, price: { register: 7.99, renew: 10.99 } },
                        { period: 36, price: { register: 6.99, renew: 9.99 } }
                    ],
                    features: [
                        { name: "Email Storage", value: "50 GB" },
                        { name: "Email Accounts", value: "1" },
                        { name: "Calendar & Contacts", value: "Yes" },
                        { name: "Mobile Sync", value: "Yes" },
                        { name: "Shared Mailboxes", value: "5" }
                    ]
                },
                {
                    id: 3,
                    name: "Email Exchange Professional",
                    description: "For professional teams. Maximum storage and enterprise features.",
                    periods: [
                        { period: 1, price: { register: 14.99, renew: 19.99 } },
                        { period: 12, price: { register: 12.99, renew: 17.99 } },
                        { period: 24, price: { register: 11.99, renew: 16.99 } },
                        { period: 36, price: { register: 10.99, renew: 15.99 } }
                    ],
                    features: [
                        { name: "Email Storage", value: "100 GB" },
                        { name: "Email Accounts", value: "1" },
                        { name: "Calendar & Contacts", value: "Yes" },
                        { name: "Mobile Sync", value: "Yes" },
                        { name: "Shared Mailboxes", value: "Unlimited" },
                        { name: "Advanced Security", value: "Yes" }
                    ]
                }
            ];
            setPlans(dummyPlans);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll('.hosting-section');

        sections.forEach((section, index) => {
            gsap.fromTo(section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected WordPress hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Zap,
            title: "Optimized Performance",
            description: "Lightning-fast WordPress hosting with optimized servers for maximum speed."
        },
        {
            icon: Shield,
            title: "Enhanced Security",
            description: "Advanced security features to protect your WordPress site from threats."
        },
        {
            icon: Server,
            title: "Auto Updates",
            description: "Automatic WordPress core, plugin, and theme updates for peace of mind."
        },
        {
            icon: Database,
            title: "Daily Backups",
            description: "Automated daily backups to ensure your data is always safe."
        },
        {
            icon: Globe,
            title: "Easy Migration",
            description: "Free WordPress site migration service to get you started quickly."
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert WordPress support available around the clock."
        }
    ];

    const microsoftEmailExchangeFAQs = [
        {
            question: "What is Microsoft Email Exchange?",
            answer: "Microsoft Email Exchange is an email hosting platform developed by Microsoft. It's a flexible option for complex business communications and data access. With this, you also get to access your Calendars, Contacts, and Tasks across all your devices."
        },
        {
            question: "What is the difference between Email Hosting and Microsoft Email Exchange?",
            answer: "Both Email Hosting and Microsoft Email Exchange let you create professional email addresses. What sets Microsoft Exchange apart is its ability to let you do the following:<br /><br />• Managing company activities<br />• Setting appointments and meetings<br />• Share calendar activities<br />• Create Business Cards<br />• Create and manage tasks<br />• Save quick notes"
        },
        {
            question: "Will you back up my emails?",
            answer: "We generally keep a backup of emails on our servers. However, we strongly recommend having your own and keeping it updated for your personal copy."
        },
        {
            question: "Can I add more mailboxes?",
            answer: "Yes, you can. Contact us for assistance in adding more mailboxes for your Microsoft Email Exchange account."
        },
        {
            question: "What is the difference between mailbox and storage?",
            answer: "Mailbox is the email account that you create. Storage, on the other hand, is the total memory space that stores emails, calendars, tasks, and contacts. With Microsoft Exchange Email, you can create one mailbox that you can use the entire storage space."
        },
        {
            question: "Can I set up an email forwarder?",
            answer: "Absolutely. You need to add the recipient email to your contacts before creating an email forwarder."
        }
    ];

    return (
         <>
            <SEO
                title="Email Exchange - SAN micron"
                description="Business-Class Emails for Your Own Domain Name. Perfect for small teams."
            />
            <div className="relative overflow-hidden min-h-screen text-white">
                {/* <Header /> */}
                <Breadcrumbs
                    title="Microsoft Email Exchange"
                    subtitle=""
                    breadcrumbText="Microsoft Email Exchange"
                    backgroundImage={BreadcrumbsImage}
                />

                {/* Hero Section */}
                <ParallaxSection speed={0.25}>
                    <Floating3D intensity={18}>
                        <section className="hosting-section container py-20 pt-32">
                            <div className="text-center max-w-4xl mx-auto">
                                {/* <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                    Microsoft Email Exchange
                                </h1> */}
                                <p className="text-xl text-textColor mb-8 leading-relaxed">
                                    Business-Class Emails for Your Own Domain Name.
                                </p>
                            </div>
                        </section>
                    </Floating3D>
                </ParallaxSection>

                {/* Pricing Plans */}
                <AnimatedSection delay={0.2} direction="up">
                    <div className="hosting-section">
                        <ProductPlansDisplay
                            plans={plans}
                            loading={loading}
                            error={error}
                            currency="USD"
                            onSelectPlan={handleSelectPlan}
                            buttonText="Get Started"
                            priceLabel="per month"
                        />
                    </div>
                </AnimatedSection>

                {/* Features Section */}
                <ParallaxSection speed={0.3} direction="down">
                    <AnimatedSection delay={0.3} direction="left">
                        <section className="hosting-section container py-20">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold mb-4">Why Choose WordPress Hosting?</h2>
                                <p className="text-textColor text-lg">Everything you need for a powerful WordPress website</p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                            <p className="text-textColor">{feature.description}</p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    </AnimatedSection>
                </ParallaxSection>

                {/* FAQ Section */}
                <AnimatedSection delay={0.6}>
                    <div className="abstract-bg hosting-section">
                        <FAQSection faqs={microsoftEmailExchangeFAQs} />
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.7}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}

